/* eslint-disable no-restricted-imports */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';

import { type LiteQuote } from '@frontend/api';
import {
  Box,
  PartThumbnail,
  CheckmarkIcon,
  Tooltip,
} from '@frontend/components';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useFeatureFlagValue } from 'store/session/hooks';

import { formatPercent, formatPrice } from 'utils/formatters';
import { formatLeadTimeLabel } from 'utils/formatters';
import { uuid } from 'utils/stringUtils';

import * as actions from '../../data/actions';
import {
  getAdditionalChargesMap,
  getAdjustment,
  getSelectedOption,
} from '../../data/reducer';
import AdditionalCharge from './AdditionalCharge';
import Adjustment from './Adjustment';

const ExistingAddOnsHeaderRow = styled.tr`
  background: #f7f7f7;
  border-radius: 2px;
  border-left: 3px solid #5b7cdb;
  border-right: 2px solid #bbb;
  padding-left: 10px;
  padding-right: 10px;
  th {
    ${(props) => props.gap && `border-top: 2px solid #fff;`}
    padding-top: 8px !important;
  }
`;

const ExistingAddOnLabelHeader = styled.th`
  text-align: left !important;
`;

const ExistingAddOnLabelCell = styled.td`
  text-align: left !important;
`;

const ExistingAddOnPriceHeader = styled.th`
  text-align: right;
`;

const ExistingAddOnPriceCell = styled.td`
  text-align: right;
`;

const ExistAddOnLeftHeader = styled.th`
  padding-left: 10px;
`;

const ExistingAddOnHeaderFiller = styled.div`
  height: 100%;
  width: 100%;
  background: #f7f7f7;
`;

const ExistingAddOnRow = styled.tr`
  background: #f7f7f7;
  border-left: 3px solid #5b7cdb;
  border-right: 2px solid #bbb;
`;

const ExistingAddOnSpacerRow = styled.tr`
  background: #f7f7f7;
  border-left: 3px solid #5b7cdb;
  border-right: 2px solid #bbb;
`;

const Footer = styled.div`
  background-color: #eee;
  border-top: 1px solid #ddd;
  padding: 13px;
  width: 100%;
`;

const FooterButton = styled.button`
  color: #06f;
  cursor: pointer;
  font-family: 'Libre Franklin';
  font-size: 12px;
  margin-right: 13px;
  outline: none;
  text-decoration: underline;

  &:disabled {
    color: #b3b3b3;
    cursor: not-allowed;
  }
`;

const Header = styled.div`
  border-bottom: 1px solid #ddd;
  display: flex;
  flex-direction: row;
`;

const PartDetails = styled.div`
  display: flex;
  flex-direction: row;
`;

const PartName = styled.div`
  color: #000;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
`;

const PartDetail = styled.span`
  color: #9b9b9b;
  font-size: 12px;
  font-weight: bold;
  margin-right: 16px;
`;

const BlueText = styled.span`
  color: #06f;
`;

const PartInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 17px;
  width: 100;
`;

const Wrapper = styled.div`
  border: 1px solid #ddd;
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  width: 100%;
`;

const ThumbnailWrapper = styled.div`
  background: linear-gradient(150deg, #fff 0%, #cccccc 100%);
  width: 83px;
`;

const OptionsTableWrapper = styled.div`
  padding-bottom: 12px;
  padding-top: 4px;
  padding-left: 10px;
  padding-right: 10px;
`;

const OptionsTable = styled.table`
  font-size: 12px;
  width: 100%;
  th {
    padding-bottom: 5px;
  }
  th,
  td {
    padding-right: 5px;
    text-align: right;
  }
`;

const CheckBoxCell = styled.td`
  text-align: center;
`;

const CheckBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 21.82px;
  justify-content: center;
  padding-left: 10px;
`;

const StyledCheckboxButton = styled.div`
  align-items: center;
  background-color: #ffffff;
  border-radius: 50%;
  border: solid 0.75px #4d79e9;
  display: flex;
  height: 12px;
  justify-content: center;
  width: 12px;

  :focus {
    border-radius: 50%;
    border: solid 0.75px #4d79e9;
    outline: none;
  }

  ${(props) =>
    props.checked &&
    `
      background-color: #4d79e9;
  `};
`;

const MainQuantityRow = styled.tr`
  ${(props) =>
    props.darkBg &&
    `
    background: #f7f7f7;
    :not(:first-of-type) td {
      border-top: 2px solid #fff;
    }
  `}
`;

type AddOnsTableProps = {
  quoteItem: any;
  quantity: any;
};

const AddOnsTable = ({ quoteItem, quantity }: AddOnsTableProps) => {
  const dispatch = useDispatch();
  const selectedOption = useSelector((state) =>
    getSelectedOption(state, quoteItem.id),
  );

  const selectAddOn = (addOn: any) => {
    dispatch(actions.selectAddOn({ addOn, quoteItemId: quoteItem.id }));
  };

  const addOnsMap = useMemo(
    () =>
      quoteItem.rootComponent.addOns?.reduce(
        (map, addOn) => ({ [addOn.id]: addOn, ...map }),
        {},
      ) ?? {},
    [quoteItem.rootComponent],
  );

  return (
    <>
      <ExistingAddOnsHeaderRow
        gap={
          quoteItem.rootComponent.discountCells?.every(
            (cell) =>
              cell.quantityId !== quantity.id ||
              (cell.manualPercentage ?? cell.percentage) === 0,
          ) ?? true
        }
      >
        <th />
        <ExistingAddOnLabelHeader colSpan="3">
          Additional Charges
        </ExistingAddOnLabelHeader>
        <ExistingAddOnPriceHeader>Price</ExistingAddOnPriceHeader>
      </ExistingAddOnsHeaderRow>
      {quoteItem.rootComponent.addOnCells
        .filter((cell) => cell.quantityId === quantity.id)
        .map((addOnCell) => (
          <ExistingAddOnRow key={addOnCell.addOnId}>
            <CheckBoxCell backgroundColor="#f7f7f7">
              <CheckBoxWrapper>
                <StyledCheckboxButton
                  checked={selectedOption.addOns[addOnCell.addOnId]}
                >
                  <CheckmarkIcon
                    color="#fff"
                    onClick={() =>
                      !addOnsMap[addOnCell.addOnId].isRequired &&
                      selectAddOn(addOnCell)
                    }
                  />
                </StyledCheckboxButton>
              </CheckBoxWrapper>
            </CheckBoxCell>
            <ExistingAddOnLabelCell colSpan="3">
              {addOnsMap[addOnCell.addOnId].name}{' '}
              {addOnsMap[addOnCell.addOnId].isRequired ? (
                <b>(Required)</b>
              ) : (
                '(Optional)'
              )}
            </ExistingAddOnLabelCell>
            <ExistingAddOnPriceCell>
              {formatPrice(
                addOnCell.manualPrice ? addOnCell.manualPrice : addOnCell.price,
                2,
              )}
            </ExistingAddOnPriceCell>
          </ExistingAddOnRow>
        ))}
    </>
  );
};

type DiscountsTableProps = {
  quoteItem: any;
  quantity: any;
};

const DiscountsTable = ({ quoteItem, quantity }: DiscountsTableProps) => {
  const discountsMap = useMemo(
    () =>
      quoteItem.rootComponent.discounts?.reduce(
        (map, discount) => ({ [discount.id]: discount, ...map }),
        {},
      ) ?? {},
    [quoteItem.rootComponent],
  );

  return (
    <>
      <ExistingAddOnsHeaderRow gap>
        <ExistAddOnLeftHeader>
          <ExistingAddOnHeaderFiller />
        </ExistAddOnLeftHeader>
        <ExistingAddOnLabelHeader colSpan="3">
          Discounts
        </ExistingAddOnLabelHeader>
        <ExistingAddOnPriceHeader>Percent</ExistingAddOnPriceHeader>
      </ExistingAddOnsHeaderRow>
      {quoteItem.rootComponent.discountCells
        .filter(
          (cell) =>
            cell.quantityId === quantity.id &&
            (cell.manualPercentage ?? cell.percentage) !== 0,
        )
        .map((discountCell) => (
          <ExistingAddOnRow
            key={`discount-row-${quantity.id}-${discountCell.discountId}`}
          >
            <CheckBoxCell backgroundColor="#f7f7f7">
              <CheckBoxWrapper>
                <StyledCheckboxButton checked>
                  <CheckmarkIcon color="#fff" />
                </StyledCheckboxButton>
              </CheckBoxWrapper>
            </CheckBoxCell>
            <ExistingAddOnLabelCell colSpan="3">
              {discountsMap[discountCell.discountId].name}
            </ExistingAddOnLabelCell>
            <ExistingAddOnPriceCell>
              {formatPercent(
                discountCell.manualPercentage ?? discountCell.percentage,
              )}
            </ExistingAddOnPriceCell>
          </ExistingAddOnRow>
        ))}
    </>
  );
};

type OptionDetailsProps = {
  quoteItem: any;
  quantity: any;
  expedite?: any;
  isSelected: boolean;
  onDeselect: () => void;
  onSelect: () => void;
  darkBg: boolean;
};

const OptionDetails = ({
  quoteItem,
  quantity,
  expedite,
  isSelected,
  onDeselect,
  onSelect,
  darkBg,
}: OptionDetailsProps) => {
  const option = expedite ?? quantity;
  const unitPrice =
    expedite?.unitPrice ??
    quantity.manualTotalUnitPrice ??
    quantity.totalUnitPrice;

  const hasDiscounts =
    quoteItem.rootComponent.discountCells?.some(
      (cell) =>
        cell.quantityId === quantity.id &&
        (cell.manualPercentage ?? cell.percentage) !== 0,
    ) ?? false;

  const hasAddOns =
    quoteItem.rootComponent.addOnCells?.some(
      (cell) => cell.quantityId === quantity.id,
    ) ?? false;

  return (
    <>
      <MainQuantityRow darkBg={darkBg}>
        <CheckBoxCell>
          <CheckBoxWrapper>
            <StyledCheckboxButton checked={isSelected}>
              <CheckmarkIcon
                color="#fff"
                onClick={() => {
                  if (isSelected) {
                    onDeselect();
                  } else {
                    onSelect();
                  }
                }}
              />
            </StyledCheckboxButton>
          </CheckBoxWrapper>
        </CheckBoxCell>
        <td>{quantity.quantity}</td>
        <td>
          {formatLeadTimeLabel(option.manualLeadTime ?? option.leadTime, {
            unit: option.leadTimeDisplayUnit,
          }).toLowerCase()}
        </td>
        <td>{`${formatPrice(
          unitPrice,
          quoteItem.rootComponent.unitPricePrecision,
        )}`}</td>
        <td>{`${formatPrice(option.totalPrice, 2)}`}</td>
      </MainQuantityRow>
      {isSelected && (
        <>
          {hasDiscounts && (
            <DiscountsTable quoteItem={quoteItem} quantity={quantity} />
          )}
          {hasAddOns && (
            <AddOnsTable quoteItem={quoteItem} quantity={quantity} />
          )}
          {(hasDiscounts || hasAddOns) && (
            <ExistingAddOnSpacerRow>
              <td colSpan="5">&nbsp;</td>
            </ExistingAddOnSpacerRow>
          )}
        </>
      )}
    </>
  );
};

const isExpediteSelected = (
  selectedOption: any,
  quantity: any,
  expedite: any,
) =>
  selectedOption?.quantity?.id === quantity.id &&
  selectedOption?.expedite?.id === expedite.id;

const isQuantitySelected = (selectedOption: any, quantity: any) =>
  selectedOption?.quantity?.id === quantity.id && !selectedOption?.expedite;

type PartSelectorProps = {
  quote: LiteQuote;
  quoteItem: any;
  index: number;
};

const PartSelector = ({ quote, quoteItem, index }: PartSelectorProps) => {
  const dispatch = useDispatch();

  const maximumQuoteItemQuantities = useFeatureFlagValue(
    featureFlagKeys.maxQuoteItemQuantities,
  );

  const additionalChargesMap = useSelector((state) =>
    getAdditionalChargesMap(state, quoteItem.id),
  );
  const adjustment = useSelector((state) => getAdjustment(state, quoteItem.id));
  const rootComponent = quoteItem.rootComponent;
  const selectedOption = useSelector((state) =>
    getSelectedOption(state, quoteItem.id),
  );

  const addAdditionalCharge = useCallback(
    (additionalCharge: any) => {
      dispatch(actions.additionalCharges.add(additionalCharge));
    },
    [dispatch],
  );

  const addAdjustment = useCallback(
    (adjustment: any) => {
      dispatch(actions.adjustments.add(adjustment));
    },
    [dispatch],
  );

  const selectOptionAction = useCallback(
    (option: any) => {
      dispatch(actions.selectOption(option));
    },
    [dispatch],
  );

  const [qtyCellWidth, setQtyCellWidth] = useState<number>();
  const [leadTimeCellWidth, setLeadTimeCellWidth] = useState<number>();
  const [unitPriceCellWidth, setUnitPriceCellWidth] = useState<number>();
  const [totalPriceCellWidth, setTotalPriceCellWidth] = useState<number>();

  const qtyHeaderRef = useRef<HTMLTableCellElement>(null);
  const leadTimeHeaderRef = useRef<HTMLTableCellElement>(null);
  const unitPriceHeaderRef = useRef<HTMLTableCellElement>(null);
  const totalPriceHeaderRef = useRef<HTMLTableCellElement>(null);

  const isSelectedOptionDiscounted = useMemo(
    () =>
      quoteItem.rootComponent.discountCells.filter(
        (cell) =>
          cell.quantityId === selectedOption?.quantity?.id &&
          (cell.manualPercentage ?? cell.percentage) !== 0,
      ).length > 0,
    [quoteItem, selectedOption],
  );

  const addOnsMap = useMemo(
    () =>
      rootComponent.addOns?.reduce(
        (map, addOn) => ({ [addOn.id]: addOn, ...map }),
        {},
      ) ?? {},
    [rootComponent],
  );

  useEffect(() => {
    setQtyCellWidth(qtyHeaderRef.current?.offsetWidth);
  }, []);

  useEffect(() => {
    setLeadTimeCellWidth(leadTimeHeaderRef.current?.offsetWidth);
  }, []);

  useEffect(() => {
    setUnitPriceCellWidth(unitPriceHeaderRef.current?.offsetWidth);
  }, []);

  useEffect(() => {
    setTotalPriceCellWidth(totalPriceHeaderRef.current?.offsetWidth);
  }, []);

  const selectOption = useCallback(
    ({ quantity, expedite }) => {
      selectOptionAction({
        quoteItemId: quoteItem.id,
        quantity,
        expedite,
        addOns: rootComponent.addOnCells
          .filter(
            (addOnCell) =>
              addOnCell.quantityId === quantity.id &&
              addOnsMap[addOnCell.addOnId]?.isRequired,
          )
          .reduce(
            (addOns, addOnCell) => ({
              ...addOns,
              [addOnCell.addOnId]: addOnCell,
            }),
            {},
          ),
      });
    },
    [addOnsMap, quoteItem.id, rootComponent.addOnCells, selectOptionAction],
  );

  const deselectOption = useCallback(() => {
    selectOptionAction({
      quoteItemId: quoteItem.id,
    });
  }, [quoteItem.id, selectOptionAction]);

  return (
    <Wrapper>
      <Header>
        <ThumbnailWrapper>
          {rootComponent.part && (
            <PartThumbnail
              containsVirus={rootComponent.part.containsVirus}
              partFileUuid={rootComponent.part.primaryFileId}
            />
          )}
        </ThumbnailWrapper>
        <PartInfo>
          <PartName>
            {`${index + 1}. `}
            {rootComponent.part.filename}
          </PartName>
          <PartDetails>
            <PartDetail>
              PART #
              <BlueText>{` ${rootComponent.partNumber || '---'}`}</BlueText>
            </PartDetail>
            <PartDetail>
              REV #<BlueText>{` ${rootComponent.revision || '---'}`}</BlueText>
            </PartDetail>
          </PartDetails>
        </PartInfo>
      </Header>
      <OptionsTableWrapper>
        <OptionsTable>
          <tr>
            <th />
            <th ref={qtyHeaderRef}>
              &nbsp;
              <br />
              Qty
            </th>
            <th ref={leadTimeHeaderRef}>
              &nbsp;
              <br />
              Lead Time
            </th>
            <th ref={unitPriceHeaderRef}>
              {isSelectedOptionDiscounted ? (
                <>
                  <Box as="span" color="feature.secondary">
                    Discounted
                  </Box>
                  <br />
                </>
              ) : (
                <>
                  &nbsp;
                  <br />
                </>
              )}
              Unit Price
            </th>
            <th ref={totalPriceHeaderRef}>
              {isSelectedOptionDiscounted ? (
                <>
                  <Box as="span" size="sm" color="feature.secondary">
                    Discounted
                  </Box>
                  <br />
                </>
              ) : (
                <>
                  &nbsp;
                  <br />
                </>
              )}
              Total Price
            </th>
          </tr>
          {rootComponent.quantities.map((quantity, quantityIndex) => (
            <>
              {quantity.expedites
                .sort((exp1, exp2) => exp1.leadTime - exp2.leadTime)
                .map((expedite, index) => (
                  <OptionDetails
                    darkBg={index === 0}
                    key={expedite.id}
                    quoteItem={quoteItem}
                    quantity={quantity}
                    expedite={expedite}
                    isSelected={isExpediteSelected(
                      selectedOption,
                      quantity,
                      expedite,
                    )}
                    onSelect={() => selectOption({ quantity, expedite })}
                    onDeselect={() => deselectOption()}
                  />
                ))}
              <OptionDetails
                darkBg={quantity.expedites.length === 0}
                quoteItem={quoteItem}
                quantity={quantity}
                isSelected={isQuantitySelected(selectedOption, quantity)}
                onSelect={() => selectOption({ quantity })}
                onDeselect={() => deselectOption()}
              />
              {quantity.expedites?.length > 0 &&
                quantityIndex !== rootComponent.quantities.length - 1 && (
                  <tr>
                    <td colSpan="5">&nbsp;</td>
                  </tr>
                )}
            </>
          ))}
        </OptionsTable>
      </OptionsTableWrapper>
      {adjustment && (
        <Adjustment
          quoteItemId={quoteItem.id}
          qtyWidth={qtyCellWidth}
          leadTimeWidth={leadTimeCellWidth}
          unitPriceWidth={unitPriceCellWidth}
          totalPriceWidth={totalPriceCellWidth}
          quote={quote}
          unitPricePrecision={quoteItem.rootComponent.unitPricePrecision}
        />
      )}
      {Object.values(additionalChargesMap).map((additionalCharge, index) => (
        <AdditionalCharge
          key={`additional-charge-${additionalCharge.id}`}
          quoteItemId={quoteItem.id}
          id={additionalCharge.id}
          name={additionalCharge.name}
          price={additionalCharge.price}
          showLabels={index === 0}
        />
      ))}
      <Footer>
        {!adjustment && (
          <Tooltip
            label={`Can't add adjustment. Line item can't have more than ${maximumQuoteItemQuantities} quantity breaks.`}
            isDisabled={
              rootComponent.quantities.length < maximumQuoteItemQuantities
            }
          >
            <FooterButton
              onClick={() => addAdjustment({ quoteItemId: quoteItem.id })}
              disabled={
                rootComponent.quantities.length >= maximumQuoteItemQuantities
              }
            >
              Add Adjustment
            </FooterButton>
          </Tooltip>
        )}
        <FooterButton
          onClick={() =>
            addAdditionalCharge({
              additionalChargeId: uuid(),
              quoteItemId: quoteItem.id,
            })
          }
        >
          Add Additional Charge
        </FooterButton>
      </Footer>
    </Wrapper>
  );
};

export default PartSelector;
